// Vista pública del calendario compartido — ADR-2026-10-15-calendario-compartido.
//
// Sin login, sin sidebar, solo lectura. El visitante ve el calendario y los
// posts, y puede dejar comentarios con su nombre. Todo pasa por funciones
// SECURITY DEFINER que validan el token.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowUp,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Instagram,
  List,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import {
  DIAS_DE_LA_SEMANA,
  correrMes,
  grillaDelMes,
  mesDe,
  tituloDelMes,
  type DiaDelMes,
} from './calendario';
import { TONOS, tonoDe } from './estadoDelPost';
import {
  comentarComoVisitante,
  fetchComentariosDeVisitante,
  fetchPostsCompartidos,
  infoDelCalendario,
  type ComentarioDeVisitante,
  type InfoCalendarioCompartido,
  type PostCompartido,
} from './compartirApi';
import { cuando, inicialDe } from '../../../utils/comentariosUi';
import type { PostDeRedes } from './post';

// ── Helpers ────────────────────────────────────────────────────────────────

function horaDe(post: PostCompartido): string {
  if (!post.cuando) return 'Sin fecha';
  return post.cuando.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function fechaLarga(d: Date): string {
  const dia = d
    .toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
    .replace(',', '');
  const hora = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return `${dia} a las ${hora}`;
}

// ── Página principal ───────────────────────────────────────────────────────

export default function CalendarioCompartido() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<InfoCalendarioCompartido | null>(null);
  const [posts, setPosts] = useState<PostCompartido[]>([]);
  const [cargando, setCargando] = useState(true);
  const [invalido, setInvalido] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const [vista, setVista] = useState<'mes' | 'lista'>('mes');

  const cargar = useCallback(async () => {
    if (!token) return;
    const i = await infoDelCalendario(token);
    if (!i) {
      setInvalido(true);
      setCargando(false);
      return;
    }
    setInfo(i);
    const p = await fetchPostsCompartidos(token);
    setPosts(p);
    setCargando(false);
  }, [token]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Polling: refresca los posts cada 30s para que los cambios del equipo
  // se vean sin recargar la página.
  useEffect(() => {
    if (invalido || !token) return;
    const id = window.setInterval(() => void cargar(), 30_000);
    return () => window.clearInterval(id);
  }, [cargar, invalido, token]);

  const postAbierto = useMemo(
    () => posts.find((p) => p.id === abierto) ?? null,
    [posts, abierto],
  );

  const delMes = useMemo(() => {
    const inicio = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const fin = new Date(mes.getFullYear(), mes.getMonth() + 1, 0, 23, 59, 59);
    return posts.filter((p) => p.cuando && p.cuando >= inicio && p.cuando <= fin);
  }, [posts, mes]);

  const grilla = useMemo(() => grillaDelMes(mes, delMes), [mes, delMes]);

  const cuenta = useMemo(() => {
    const c = { listo: 0, falta: 0, publicado: 0, error: 0 };
    for (const p of delMes) c[tonoDe(p as unknown as PostDeRedes)]++;
    return c;
  }, [delMes]);

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando calendario…
        </div>
      </div>
    );
  }

  if (invalido || !info || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
        <div className="mx-auto max-w-md px-6 text-center">
          <CalendarDays className="mx-auto w-10 h-10 text-neutral-300" />
          <h1 className="mt-4 text-lg font-semibold text-neutral-800">
            Este link no es válido o fue revocado
          </h1>
          <p className="mt-2 text-sm text-neutral-500">
            Pedile a quien te lo mandó que te envíe uno nuevo.
          </p>
        </div>
      </div>
    );
  }

  if (postAbierto) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <DetalleDePost
          post={postAbierto}
          token={token}
          nombreMarca={info.nombreMarca}
          logoUrl={info.logoUrl}
          onVolver={() => setAbierto(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="mx-auto max-w-5xl py-6 px-4 md:py-10 md:px-8">
        {/* Encabezado */}
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white shadow-sm">
            <Instagram className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-neutral-800 md:text-2xl">
              {info.nombreMarca}
            </h1>
            <p className="text-sm text-neutral-500">Calendario de Instagram</p>
          </div>
        </div>

        {/* Resumen */}
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Dato valor={delMes.length} etiqueta="Este mes" />
          <Dato valor={cuenta.listo} etiqueta="Listos" punto={TONOS.listo.punto} />
          <Dato valor={cuenta.falta} etiqueta="A completar" punto={TONOS.falta.punto} />
          <Dato valor={cuenta.publicado} etiqueta="Publicados" punto={TONOS.publicado.punto} />
        </div>

        {/* Controles del mes */}
        <div className="mt-5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <h2 className="truncate text-lg font-semibold capitalize text-neutral-800">
              {tituloDelMes(mes)}
            </h2>
            <button
              type="button"
              onClick={() => setMes((m) => correrMes(m, -1))}
              className="ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMes((m) => correrMes(m, 1))}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex rounded-xl bg-neutral-100 p-1">
            {(['mes', 'lista'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVista(v)}
                className={`flex items-center gap-1.5 rounded-lg py-1.5 px-2.5 text-xs font-medium transition-colors ${
                  vista === v ? 'bg-white text-neutral-800 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {v === 'mes' ? <CalendarDays className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}
                {v === 'mes' ? 'Mes' : 'Lista'}
              </button>
            ))}
          </div>
        </div>

        {vista === 'lista' ? (
          <ListaPublica posts={delMes} onAbrir={setAbierto} />
        ) : (
          <div className="mt-3 overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-sm">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50">
                {DIAS_DE_LA_SEMANA.map((d, i) => (
                  <div
                    key={d}
                    className={`py-2.5 px-1 text-center text-[11px] font-semibold uppercase tracking-wide ${
                      i >= 5 ? 'text-neutral-400' : 'text-neutral-500'
                    }`}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {grilla.map((dia) => (
                  <CasillaPublica key={dia.fecha.toISOString()} dia={dia} onAbrir={setAbierto} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Leyenda */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
          {(['listo', 'falta', 'publicado', 'error'] as const).map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${TONOS[t].punto}`} />
              {TONOS[t].texto}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Piezas del calendario ──────────────────────────────────────────────────

function Dato({ valor, etiqueta, punto }: { valor: number; etiqueta: string; punto?: string }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white py-3 px-4 shadow-sm">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
        {punto && <span className={`h-1.5 w-1.5 rounded-full ${punto}`} />}
        {etiqueta}
      </p>
      <p className="mt-1 text-xl font-semibold text-neutral-800">{valor}</p>
    </div>
  );
}

function CasillaPublica({
  dia,
  onAbrir,
}: {
  dia: DiaDelMes<PostCompartido>;
  onAbrir: (id: string) => void;
}) {
  const hoy = dia.esHoy;
  return (
    <div
      className={`min-h-[100px] border-b border-r border-neutral-100 p-1 ${
        !dia.delMes ? 'bg-neutral-50/60' : ''
      }`}
    >
      <span
        className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium ${
          hoy
            ? 'bg-brand-primary text-white'
            : dia.delMes
              ? 'text-neutral-800'
              : 'text-neutral-400'
        }`}
      >
        {dia.fecha.getDate()}
      </span>
      <div className="space-y-0.5">
        {dia.posts.slice(0, 3).map((post) => {
          const tono = TONOS[tonoDe(post as unknown as PostDeRedes)];
          return (
            <button
              key={post.id}
              type="button"
              onClick={() => onAbrir(post.id)}
              className={`flex w-full items-center gap-1.5 rounded-lg border-l-[3px] py-1 pr-1.5 pl-1 text-left transition-colors ${tono.pildora}`}
            >
              <span className="relative grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md bg-white">
                {post.miniaturas[0] ? (
                  <CachedImg src={post.miniaturas[0]} alt="" className="h-full w-full object-cover" />
                ) : (
                  <CalendarDays className="w-3 h-3 text-neutral-400" />
                )}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-neutral-800">
                {horaDe(post)}
              </span>
            </button>
          );
        })}
        {dia.posts.length > 3 && (
          <p className="text-center text-[10px] text-neutral-400">
            +{dia.posts.length - 3} más
          </p>
        )}
      </div>
    </div>
  );
}

function ListaPublica({
  posts,
  onAbrir,
}: {
  posts: PostCompartido[];
  onAbrir: (id: string) => void;
}) {
  if (posts.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-neutral-300 py-12 text-center">
        <CalendarDays className="mx-auto w-8 h-8 text-neutral-300" />
        <p className="mt-3 text-sm text-neutral-500">No hay posts este mes</p>
      </div>
    );
  }
  return (
    <div className="mt-3 divide-y divide-neutral-100 rounded-2xl border border-neutral-200 bg-white shadow-sm">
      {posts.map((post) => {
        const tono = TONOS[tonoDe(post as unknown as PostDeRedes)];
        return (
          <button
            key={post.id}
            type="button"
            onClick={() => onAbrir(post.id)}
            className="flex w-full items-center gap-3 py-3 px-4 text-left transition-colors hover:bg-neutral-50"
          >
            <span className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-neutral-100">
              {post.miniaturas[0] ? (
                <CachedImg src={post.miniaturas[0]} alt="" className="h-full w-full object-cover" />
              ) : (
                <CalendarDays className="w-5 h-5 text-neutral-400" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-neutral-800">
                {post.cuando ? fechaLarga(post.cuando) : 'Sin fecha'}
              </span>
              <span className="mt-0.5 block truncate text-xs text-neutral-500">
                {post.texto || 'Sin descripción'}
              </span>
            </span>
            <span className={`h-2 w-2 shrink-0 rounded-full ${tono.punto}`} />
          </button>
        );
      })}
    </div>
  );
}

// ── Detalle de un post (read-only) ─────────────────────────────────────────

function DetalleDePost({
  post,
  token,
  nombreMarca,
  logoUrl,
  onVolver,
}: {
  post: PostCompartido;
  token: string;
  nombreMarca: string;
  logoUrl: string | null;
  onVolver: () => void;
}) {
  const [slide, setSlide] = useState(0);
  const imagenes = post.miniaturas.filter((u) => !!u);
  const total = imagenes.length;

  return (
    <div className="mx-auto max-w-5xl py-4 px-4 md:py-10 md:px-8">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onVolver}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-neutral-800 hover:bg-neutral-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="truncate text-lg font-semibold text-neutral-800 md:text-xl">
          {post.cuando ? fechaLarga(post.cuando) : 'Post sin fecha'}
        </h1>
      </div>

      <div className="mt-4 grid items-start gap-5 md:mt-5 md:gap-6 md:grid-cols-2">
        {/* Post estilo Instagram */}
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm md:sticky md:top-4">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2.5">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-8 w-8 rounded-full border border-neutral-200 object-cover"
              />
            ) : (
              <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-xs font-bold text-white">
                {inicialDe(nombreMarca)}
              </span>
            )}
            <span className="text-sm font-semibold text-neutral-800">{nombreMarca.toLowerCase()}</span>
          </div>

          {/* Carrusel de imágenes */}
          <div
            className="relative aspect-square w-full bg-neutral-100 touch-pan-y"
            onPointerDown={(e) => {
              if (total <= 1) return;
              const startX = e.clientX;
              const el = e.currentTarget;
              let moved = false;
              const onMove = (ev: PointerEvent) => {
                const dx = ev.clientX - startX;
                if (Math.abs(dx) > 40 && !moved) {
                  moved = true;
                  if (dx < 0 && slide < total - 1) setSlide((s) => s + 1);
                  if (dx > 0 && slide > 0) setSlide((s) => s - 1);
                }
              };
              const onUp = () => {
                el.releasePointerCapture(e.pointerId);
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
              };
              el.setPointerCapture(e.pointerId);
              window.addEventListener('pointermove', onMove);
              window.addEventListener('pointerup', onUp);
            }}
          >
            {total > 0 ? (
              <img
                src={imagenes[slide]}
                alt=""
                className="pointer-events-none h-full w-full select-none object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <CalendarDays className="w-10 h-10 text-neutral-300" />
              </div>
            )}

            {/* Flechas de navegación */}
            {total > 1 && slide > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSlide((s) => s - 1); }}
                className="absolute left-3 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-800 shadow-lg transition-transform hover:scale-110 active:scale-95"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {total > 1 && slide < total - 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSlide((s) => s + 1); }}
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-800 shadow-lg transition-transform hover:scale-110 active:scale-95"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}

            {/* Indicador de posición */}
            {total > 1 && (
              <div className="absolute top-3 right-3 rounded-full bg-black/60 py-0.5 px-2.5 text-[11px] font-medium text-white">
                {slide + 1}/{total}
              </div>
            )}
          </div>

          {/* Dots del carrusel */}
          {total > 1 && (
            <div className="flex justify-center gap-1.5 py-4">
              {imagenes.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                    i === slide ? 'bg-brand-primary' : 'bg-neutral-300'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Texto y fecha */}
          <div className="px-3 pb-3 pt-1">
            {post.texto && (
              <p className="text-xs leading-relaxed text-neutral-700">
                <span className="font-semibold">{nombreMarca.toLowerCase()}</span>{' '}
                {post.texto}
              </p>
            )}
            {post.cuando && (
              <p className="mt-2 text-[10px] uppercase text-neutral-400">
                Sale el {post.cuando.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                {' · '}
                {post.cuando.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>

        {/* Comentarios */}
        <div className="md:sticky md:top-4">
          <ComentariosDeVisitante token={token} postId={post.id} />
        </div>
      </div>
    </div>
  );
}

// ── Comentarios de visitante ───────────────────────────────────────────────

function ComentariosDeVisitante({ token, postId }: { token: string; postId: string }) {
  const [comentarios, setComentarios] = useState<ComentarioDeVisitante[]>([]);
  const [cargando, setCargando] = useState(true);
  const [nombre, setNombre] = useState(() => {
    try { return localStorage.getItem('atlas:visitante:nombre') ?? ''; } catch { return ''; }
  });
  const [texto, setTexto] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    const c = await fetchComentariosDeVisitante(token, postId);
    setComentarios(c);
    setCargando(false);
  }, [token, postId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Polling: refresca comentarios cada 15s
  useEffect(() => {
    const id = window.setInterval(() => void cargar(), 15_000);
    return () => window.clearInterval(id);
  }, [cargar]);

  // Auto-resize textarea
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [texto]);

  const puede = nombre.trim() !== '' && texto.trim() !== '' && !trabajando;

  const mandar = async () => {
    if (!puede) return;
    setTrabajando(true);
    setError(null);
    // Guardar nombre para la próxima vez
    try { localStorage.setItem('atlas:visitante:nombre', nombre.trim()); } catch {}
    const r = await comentarComoVisitante(token, postId, nombre, texto);
    if (r.error) {
      setError(r.error);
    } else {
      setTexto('');
      await cargar();
    }
    setTrabajando(false);
  };

  const pendientes = comentarios.filter((c) => !c.resuelto);
  const hechos = comentarios.filter((c) => c.resuelto);

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
        <MessageSquare className="w-4 h-4 text-neutral-500" />
        Comentarios
      </h3>
      <p className="mt-1 text-[13px] leading-relaxed text-neutral-500">
        Dejá tus sugerencias sobre este post. El equipo las va a ver.
      </p>

      {/* Compositor */}
      <div className="mt-3 space-y-2">
        <input
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Tu nombre"
          maxLength={100}
          className="w-full rounded-xl border border-neutral-200 py-2 px-3 text-sm text-neutral-800 outline-none placeholder:text-neutral-400 focus:border-brand-primary focus:ring-2 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.07)]"
        />
        <div className="flex items-end gap-1 rounded-2xl border border-neutral-200 bg-white p-1 transition-colors focus-within:border-brand-primary">
          <textarea
            ref={areaRef}
            rows={1}
            value={texto}
            maxLength={2000}
            placeholder="Ej: falta la foto de atrás, y el precio va con IVA"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void mandar();
              }
            }}
            className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-3 py-2.5 text-[13px] leading-5 text-neutral-800 outline-none placeholder:text-neutral-400"
          />
          <button
            type="button"
            disabled={!puede}
            onClick={() => void mandar()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white transition-opacity disabled:opacity-40"
          >
            {trabajando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-2 rounded-xl bg-[#FEE7E7] py-2 px-3 text-[13px] text-[#B91C1C]">{error}</p>
      )}

      {/* Lista */}
      <div className="mt-4">
        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : comentarios.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 py-8 px-4 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-neutral-300" />
            <p className="mt-3 text-sm font-semibold text-neutral-800">
              Todavía no hay comentarios
            </p>
            <p className="mx-auto mt-1 max-w-xs text-[13px] text-neutral-500">
              Anotá acá lo que haya que corregir y no se te va a perder.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendientes.length > 0 && (
              <ul className="space-y-4">
                {pendientes.map((c) => (
                  <ComentarioItem key={c.id} c={c} />
                ))}
              </ul>
            )}
            {hechos.length > 0 && (
              <div>
                <p className="mb-3 text-[13px] font-medium text-neutral-400">
                  Hechos ({hechos.length})
                </p>
                <ul className="space-y-4">
                  {hechos.map((c) => (
                    <ComentarioItem key={c.id} c={c} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ComentarioItem({ c }: { c: ComentarioDeVisitante }) {
  return (
    <li className="flex gap-2.5">
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[15px] font-semibold ${
          c.resuelto
            ? 'bg-neutral-300 text-white'
            : c.esVisitante
              ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary'
              : 'bg-brand-primary text-white'
        }`}
      >
        {inicialDe(c.nombreVisitante)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-neutral-800">
            {c.nombreVisitante}
            {c.esVisitante && (
              <span className="ml-1 text-[12px] font-medium text-brand-primary">(visitante)</span>
            )}
          </span>
          <span className="shrink-0 text-[13px] text-neutral-400">{cuando(c.createdAt)}</span>
        </div>
        <p
          className={`whitespace-pre-wrap break-words text-sm leading-normal ${
            c.resuelto ? 'text-neutral-400 line-through' : 'text-neutral-800'
          }`}
        >
          {c.texto}
        </p>
      </div>
      {c.resuelto && (
        <Check className="mt-1 h-4 w-4 shrink-0 text-emerald-500" />
      )}
    </li>
  );
}
